import type { ResellerPermission, RoleMatrix } from "@xop-any-time/shared";
import { GRANTABLE_PERMISSIONS } from "@xop-any-time/shared";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useState, type FormEvent, type JSX } from "react";

import { ConfirmDialog } from "../../components/ui/confirm-dialog.js";
import { roleErrorMessage } from "./roleErrors.js";

/**
 * The company's role matrix (spec F §6.4): the editable roles × the grantable
 * keys. Owner is not a row — it is everything. A tick saves at once (one PUT
 * per change, the whole row) — the raw picked set is sent as-is; crm-api
 * applies the SAME closure rule on receipt and the refreshed matrix (after
 * invalidation) reflects whatever it re-derives, so a permission an implier
 * still forces on reappears checked rather than silently vanishing
 * client-side. "Reset to default" deletes the company's row so the shipped
 * default applies again — including future changes to that default.
 *
 * CUSTOM ROLES (2026-09-15). Rows follow the matrix's `order`, which includes
 * the company's own roles. A custom row has no default to reset to; it offers
 * Rename and Delete instead. Delete goes through `ConfirmDialog`, and a role
 * someone still holds is refused by crm-api (409 ROLE_IN_USE) — the page says
 * how many people to move first.
 *
 * `readOnly` is how a non-owner sees it: every row, nothing to press. Hiding
 * the controls is a courtesy; crm-api refuses a non-owner's write regardless.
 */
export function RoleMatrixCard({
  matrix,
  saving,
  readOnly = false,
  onChange,
  onReset,
  onRename,
  onDelete,
}: {
  matrix: RoleMatrix;
  saving: boolean;
  readOnly?: boolean;
  onChange?: (role: string, permissions: ResellerPermission[]) => void;
  onReset?: (role: string) => void;
  /** Rejects with the server's refusal, which the rename dialog shows. */
  onRename?: (role: string, name: string, permissions: ResellerPermission[]) => Promise<unknown>;
  /** Resolves either way; the caller reports a refusal. */
  onDelete?: (role: string) => Promise<unknown>;
}): JSX.Element {
  const [renaming, setRenaming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const rows = matrix.order.filter((role) => matrix.roles[role] !== undefined);

  return (
    <Card>
      <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
        <Box sx={{ px: 2.5, pt: 2 }}>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            Owners can do everything and cannot be narrowed. Ticking <code>edit</code> also grants <code>view</code>;
            Licences and invoice editing also grant Customers view.
          </Typography>
        </Box>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Role</TableCell>
                {GRANTABLE_PERMISSIONS.map((k) => (
                  <TableCell key={k} align="center" sx={{ fontSize: 11, whiteSpace: "nowrap" }}>{k}</TableCell>
                ))}
                {!readOnly && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((role) => {
                const row = matrix.roles[role]!;
                const held = new Set(row.permissions);
                return (
                  <TableRow key={role} data-testid={`matrix-row-${role}`}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {row.label}
                      {row.custom ? (
                        <Chip label="custom" size="small" variant="outlined" sx={{ ml: 1, height: 18, fontSize: 11 }} />
                      ) : (
                        row.isDefault && <Box component="span" sx={{ ml: 1, fontSize: 11, color: "text.disabled" }}>default</Box>
                      )}
                    </TableCell>
                    {GRANTABLE_PERMISSIONS.map((k) => (
                      <TableCell key={k} align="center" padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={held.has(k)}
                          disabled={saving || readOnly}
                          inputProps={{ "aria-label": `${k} for ${row.label}` }}
                          onChange={(e) => {
                            const next = new Set(held);
                            if (e.target.checked) next.add(k); else next.delete(k);
                            // Sent RAW — crm-api is the one that closes it
                            // (spec F §6.4); closing here first would let a
                            // deselected implied key silently reappear
                            // without the person seeing why.
                            onChange?.(role, GRANTABLE_PERMISSIONS.filter((p) => next.has(p)));
                          }}
                        />
                      </TableCell>
                    ))}
                    {!readOnly && (
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {row.custom ? (
                          <>
                            <Button size="small" disabled={saving} onClick={() => setRenaming(role)}>Rename</Button>
                            <Button size="small" color="error" disabled={saving} onClick={() => setDeleting(role)}>Delete</Button>
                          </>
                        ) : (
                          !row.isDefault && (
                            <Button size="small" disabled={saving} onClick={() => onReset?.(role)}>Reset to default</Button>
                          )
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </CardContent>

      {renaming !== null && matrix.roles[renaming] && (
        <RenameRoleDialog
          currentName={matrix.roles[renaming]!.label}
          onClose={() => setRenaming(null)}
          onSubmit={(name) => onRename?.(renaming, name, matrix.roles[renaming]!.permissions) ?? Promise.resolve()}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        title={`Delete the ${deleting ? (matrix.roles[deleting]?.label ?? "") : ""} role?`}
        description="The role and its permissions are removed from your company. A role someone still has cannot be deleted — move them to another role first."
        confirmLabel="Delete role"
        onConfirm={async () => {
          // The caller reports a refusal above the matrix; the dialog closes
          // either way so the message is not hidden behind it.
          if (deleting) await onDelete?.(deleting);
        }}
      />
    </Card>
  );
}

function RenameRoleDialog({
  currentName,
  onClose,
  onSubmit,
}: {
  currentName: string;
  onClose: () => void;
  onSubmit: (name: string) => Promise<unknown>;
}): JSX.Element {
  const [name, setName] = useState(currentName);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const trimmed = name.trim();

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (trimmed === "" || trimmed === currentName) return;
    setWorking(true);
    setError(null);
    try {
      await onSubmit(trimmed);
      onClose();
    } catch (err) {
      setError(roleErrorMessage(err, "We could not rename the role. Try again."));
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open onClose={() => !working && onClose()} maxWidth="xs" fullWidth>
      <Box component="form" onSubmit={submit}>
        <DialogTitle>Rename role</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {error && <Alert severity="error" role="alert">{error}</Alert>}
          <TextField
            autoFocus
            label="Role name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: 60 }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose} disabled={working}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={working || trimmed === "" || trimmed === currentName}>
            Save
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
